/**
 * A guest's points, at the till.
 *
 * With a guest on the tab the waiter sees what they hold and can spend it on
 * this bill: all they are allowed to in one tap, or a number typed in, or
 * something from the rewards list. It comes off as money off, "Points", and
 * can be given back from here until the bill is paid.
 *
 * The server says what the points are worth, how many may go on this tab and
 * why a reward cannot be had. Nothing here adds money up.
 */

import { useState } from 'react';
import {
  formatMoney,
  useGiveBackPoints,
  useLoyaltyAccount,
  useRedeemPoints,
  useRedeemReward,
  useTabLoyalty,
  type Order,
} from '@restaurant/api-client';
import {
  ActionError,
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Sheet,
} from '@restaurant/ui';

/** "240 points" beside the guest's name on the tab. */
export function PointsBadge({ customerId }: { customerId: string }) {
  const account = useLoyaltyAccount(customerId);
  if (!account.data) return null;
  return (
    <span className="gs__points">
      {account.data.balance} point{account.data.balance === 1 ? '' : 's'}
      {account.data.tier ? ` · ${account.data.tier.name}` : ''}
    </span>
  );
}

export function PointsSheet({ order, onDone }: { order: Order; onDone: () => void }) {
  const tab = useTabLoyalty(order.id);
  const redeem = useRedeemPoints();
  const reward = useRedeemReward();
  const giveBack = useGiveBackPoints();
  const [typed, setTyped] = useState('');
  const busy = redeem.isPending || reward.isPending || giveBack.isPending;

  return (
    <Sheet title={`Points on #${order.number}`} onDismiss={onDone}>
      {tab.isPending ? (
        <Loading label="Reading their points" />
      ) : tab.isError ? (
        <ErrorNotice message={tab.error.message} />
      ) : tab.data.account === null ? (
        <Empty title="Nobody on this tab" description="Name the guest first." />
      ) : (
        <>
          <p className="pt__balance">
            <span className="pt__count tabular">{tab.data.account.balance}</span> points
            {tab.data.account.tier && <Badge tone="accent">{tab.data.account.tier.name}</Badge>}
          </p>
          {!tab.data.active ? (
            <p className="os__sheet-note">
              Points are switched off here. A manager can turn them on.
            </p>
          ) : (
            <>
              <p className="os__sheet-note">
                {tab.data.redeem_points} points take {formatMoney(tab.data.redeem_value)} off
                {tab.data.min_redeem > 0 ? `, at least ${tab.data.min_redeem} at a time` : ''}.
              </p>
              {tab.data.max_points > 0 ? (
                <Button
                  variant="primary"
                  size="lg"
                  block
                  disabled={busy}
                  loading={redeem.isPending && redeem.variables?.points === tab.data.max_points}
                  onClick={() =>
                    redeem.mutate({ orderId: order.id, points: tab.data.max_points })
                  }
                >
                  Use {tab.data.max_points} points, −{formatMoney(tab.data.max_value)}
                </Button>
              ) : (
                <p className="os__sheet-note">Nothing more can go on this bill in points.</p>
              )}
              {tab.data.max_points > 0 && (
                <div className="pt__typed">
                  <Field label="Or this many">
                    <Input
                      value={typed}
                      inputMode="numeric"
                      onChange={(e) => setTyped(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                  </Field>
                  <Button
                    size="lg"
                    disabled={busy || Number(typed) <= 0}
                    onClick={() =>
                      redeem.mutate(
                        { orderId: order.id, points: Number(typed) },
                        { onSuccess: () => setTyped('') },
                      )
                    }
                  >
                    Use these
                  </Button>
                </div>
              )}
              <ActionError
                error={redeem.error}
                action="Spending points"
                title="Not taken off"
              />

              {tab.data.rewards.length > 0 && (
                <>
                  <p className="gf__label">Rewards</p>
                  <ul className="pt__rewards">
                    {tab.data.rewards.map(({ reward: r, unavailable }) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          className="pt__reward"
                          disabled={busy || unavailable !== null}
                          onClick={() => reward.mutate({ orderId: order.id, rewardId: r.id })}
                        >
                          <span className="pt__reward-name">{r.name}</span>
                          <span className="pt__reward-cost tabular">{r.points} points</span>
                          {unavailable && <span className="pt__why">{unavailable}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <ActionError
                    error={reward.error}
                    action="Spending points"
                    title="Not taken off"
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      {tab.data && tab.data.redeemed.length > 0 && (
        <>
          <p className="gf__label">Spent on this tab</p>
          <ul className="pt__spent">
            {tab.data.redeemed.map((row) => (
              <li key={row.discount_id} className="pt__spent-row">
                <span>
                  {row.note ?? `${row.points} points`}
                  {row.note ? ` · ${row.points} points` : ''}
                </span>
                <span className="tabular">−{formatMoney(row.amount)}</span>
                <Button
                  size="sm"
                  disabled={busy || order.state !== 'open'}
                  onClick={() =>
                    giveBack.mutate({ orderId: order.id, discountId: row.discount_id })
                  }
                >
                  Give back
                </Button>
              </li>
            ))}
          </ul>
          <ActionError
            error={giveBack.error}
            action="Giving the points back"
            title="Still spent"
          />
        </>
      )}
    </Sheet>
  );
}
